import { Component } from "@theme/component";
/**
 *
 * @typedef {object} FormRefs
 * @property {HTMLInputElement} inputField - The input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @property {HTMLDivElement} errorContainer - The error container element.
 * @extends {Component<FormRefs>}
 */
class FormInput extends Component {
  requiredRefs = ["inputField"];
  optionalRefs = ["resetButton", "errorContainer"];

  connectedCallback() {
    super.connectedCallback();

    this.refs.inputField?.addEventListener("invalid", this.handleInvalid);
  }

  disconnectedCallback() {
    this.refs.inputField?.removeEventListener("invalid", this.handleInvalid);

    super.disconnectedCallback();
  }

  /**
   * @param {Event} event
   */
  handleInvalid = (event) => {
    event.preventDefault();

    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input) return;

    let message = input.validationMessage;

    if (input.validity.valueMissing) {
      message = input.dataset.errorValueMissing || "Please enter your email address.";
    } else if (input.validity.typeMismatch || input.validity.patternMismatch) {
      message = input.dataset.errorInvalid || "Please enter a valid email address.";
    }

    this.showError(message);
  };

  /**
   * @param {string} message
   */
  showError(message) {
    if (!this.refs.errorContainer) return;

    this.classList.add("input-has-error");
    this.classList.remove("input-is-valid");
    this.refs.errorContainer.textContent = message;
  }

  clearError() {
    if (!this.refs.errorContainer) return;

    this.classList.remove("input-has-error");
    this.refs.errorContainer.textContent = "";
  }

  validateInput() {
    if (this.refs.inputField.validity.valid && this.inputHasValue) {
      this.classList.add("input-is-valid");
      this.classList.remove("input-has-error");
      this.clearError();
    } else {
      this.classList.remove("input-is-valid");
    }
  }
  get inputHasValue() {
    return this.refs.inputField?.value?.length > 0;
  }
  toggleResetButton() {
    if (!this.refs.resetButton) return;
    this.refs.resetButton.classList.toggle("hidden", !this.inputHasValue);
  }

  handleInputChange() {
    this.closest("form")?.toggleAttribute("data-typing", true);
    this.toggleResetButton();
    this.clearError();
    this.validateInput();
  }

  handleResetClick() {
    this.refs.inputField.value = "";
    this.refs.inputField.focus();
    this.toggleResetButton();
    this.classList.remove("input-is-valid");
    this.clearError();
  }
}

if (!customElements.get("form-input")) {
  customElements.define("form-input", FormInput);
}
